import React, { useContext, useEffect, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useTheme } from 'styled-components';

import { isDefined } from '../../../../../utils/variables';
import { GeodataSetupContext } from '../../../../state/geodata-setup-state';
import { DataTable, DataTableColumns, DataTableSortType } from '../../../data-table/data-table';
import { FileSelectionButton } from '../../../file-selection-button/file-selection-button';
import { Icon } from '../../../icon/icon';
import { IconPanel } from '../../../icon-panel/icon-panel';
import { InfoBox } from '../../../info-box/info-box';
import { Stack } from '../../../stack/stack';
import { supportedBoundaryPolygonFileTypes } from '../../gcp-page/boundary-polygon-list';

type TableItem = {
  statusIcon: React.ReactNode;
  filename: string;
  actions: React.ReactNode;
  // Keep original data for reference
  originalData: { filename: string; file: File };
};

const UploadBoundary = () => {
  const { t } = useTranslation('cloudProcessing');
  const theme = useTheme();
  const context = useContext(GeodataSetupContext);
  const [error, setError] = React.useState<string | null>(null);
  const [boundaryFiles, setBoundaryFiles] = React.useState<{ filename: string; file: File }[]>([]);

  // Load existing boundary files from context on mount
  useEffect(() => {
    if (isDefined(context)) {
      const existingFiles = context.boundaryFiles;
      if (existingFiles.length > 0) {
        setBoundaryFiles(existingFiles);
      }
    }
  }, [context]);

  // dragCount holds number of elements we have currently dragged into.
  // As long as dragCounts > 0 we are dragging over the droppable area.
  const dragCount = React.useRef(0);
  const [isDropable, setIsDropable] = React.useState(false);

  const handleFilesSelected = React.useCallback(
    (files: File[]) => {
      setError(null);

      const validExtensions = supportedBoundaryPolygonFileTypes.split(',');
      const newFiles: { filename: string; file: File }[] = [];
      const errors: string[] = [];

      for (const file of files) {
        // Validate file extension
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!validExtensions.includes(fileExtension)) {
          errors.push(
            `${file.name}: ${t('geodataSetup.uploadBoundary.invalidFileType', { ns: 'cloudProcessing' })}`,
          );
          continue;
        }

        const newFile = {
          filename: file.name,
          file: file,
        };

        // Check if file already exists
        const exists = boundaryFiles.some(
          (f) => f.filename === newFile.filename && f.file.size === newFile.file.size,
        );
        if (exists) {
          errors.push(
            `${file.name}: ${t('geodataSetup.uploadBoundary.fileAlreadyAdded', { ns: 'cloudProcessing' })}`,
          );
          continue;
        }

        newFiles.push(newFile);
      }

      if (errors.length > 0) {
        setError(errors.join(', '));
      }

      if (newFiles.length > 0) {
        const updatedFiles = [...boundaryFiles, ...newFiles];
        setBoundaryFiles(updatedFiles);

        if (isDefined(context)) {
          context.setBoundaryFiles(updatedFiles);
        }
      }
    },
    [boundaryFiles, context, t],
  );

  const { getRootProps } = useDropzone({
    noClick: true, // Prevents file picker from being displayed when clicking on inner elements.
    accept: {
      'application/vnd.google-earth.kml+xml': ['.kml'],
      'application/vnd.google-earth.kmz': ['.kmz'],
    },
    multiple: true,
    // Note: .shp, .dxf, .dwg files don't have standard MIME types, so we validate by extension in handleFilesSelected
    onDrop: React.useCallback(
      (acceptedFiles: File[], rejectedFiles: { file?: File }[]) => {
        dragCount.current = 0;
        setIsDropable(false);
        const allFiles: File[] = [...acceptedFiles];

        // Try to handle files that were rejected due to MIME type but might still be valid
        for (const rejectedFile of rejectedFiles) {
          if (rejectedFile.file) {
            allFiles.push(rejectedFile.file);
          }
        }

        if (allFiles.length > 0) {
          handleFilesSelected(allFiles);
        } else if (rejectedFiles.length > 0) {
          setError(t('geodataSetup.uploadBoundary.invalidFileType', { ns: 'cloudProcessing' }));
        }
      },
      [handleFilesSelected, t],
    ),
    onDragEnter: (e) => {
      e.preventDefault();
      dragCount.current++;
      setIsDropable(true);
    },
    onDragLeave: () => {
      dragCount.current--;
      if (dragCount.current === 0) {
        setIsDropable(false);
      }
    },
  });

  const handleRemoveFile = React.useCallback(
    (index: number) => {
      const newFiles = boundaryFiles.filter((_, i) => i !== index);
      setBoundaryFiles(newFiles);

      if (isDefined(context)) {
        context.setBoundaryFiles(newFiles);
      }
    },
    [boundaryFiles, context],
  );

  const selectFilesButton = (
    <FileSelectionButton
      accept={supportedBoundaryPolygonFileTypes}
      color="secondary"
      leftIcon={{ icon: ['fad', 'upload'] }}
      multiple={true}
      text={t('fileManager.uploadFilesDialog.chooseFilesButton', { ns: 'components' })}
      variant="contained"
      onFilesSelected={(files) => {
        if (files.length > 0) {
          handleFilesSelected(files);
        }
      }}
    />
  );

  const columns = useMemo<DataTableColumns<TableItem>>(
    () => ({
      statusIcon: {
        title: t('geodataSetup.uploadBoundary.columns.status', { ns: 'cloudProcessing' }),
        alignment: 'center',
        width: '60px',
        unfilterable: true,
        render: (item: TableItem) => item.statusIcon,
      },
      filename: {
        title: t('geodataSetup.uploadBoundary.columns.filename', { ns: 'cloudProcessing' }),
        sortableBy: { type: DataTableSortType.STRING },
        unfilterable: true,
        alignment: 'left',
        render: (item: TableItem) => item.filename,
      },
      actions: {
        title: '',
        alignment: 'center',
        width: '60px',
        unfilterable: true,
        render: (item: TableItem) => item.actions,
      },
    }),
    [t],
  );

  const tableItems = useMemo<TableItem[]>(() => {
    return boundaryFiles.map((fileData, index) => ({
      originalData: fileData,
      filename: fileData.filename,
      statusIcon: (
        <Icon
          color={theme.color.green}
          icon={['fas', 'check-circle']}
          title={t('geodataSetup.uploadBoundary.valid', { ns: 'cloudProcessing' })}
        />
      ),
      actions: (
        <Icon
          color={theme.color.gray.medium}
          icon={['fas', 'trash-alt']}
          style={{ cursor: 'pointer' }}
          title={t('geodataSetup.uploadBoundary.removeFile', { ns: 'cloudProcessing' })}
          onClick={() => handleRemoveFile(index)}
        />
      ),
    }));
  }, [boundaryFiles, theme, handleRemoveFile, t]);

  const titleText = useMemo(() => {
    return `${t('geodataSetup.uploadBoundary.heading', { ns: 'cloudProcessing' })} (${boundaryFiles.length})`;
  }, [boundaryFiles.length, t]);

  const fileTypesList = useMemo(() => {
    return supportedBoundaryPolygonFileTypes.split(',').join(', ');
  }, []);

  return (
    <Container>
      <Content {...getRootProps()}>
        {boundaryFiles.length === 0 && (
          <Stack height="100%">
            <h4>{titleText}</h4>
            <DragAndDropInfo>
              <IconPanel
                header={{ icon: { icon: ['fad', 'map'], opacity: '50%', size: '5x' } }}
                responsiveness={{ compressed: false }}
              >
                {{
                  title: (
                    <Stack>
                      <span>
                        {t('geodataSetup.uploadBoundary.addBoundaryPolygonsTitle', {
                          fileTypes: fileTypesList,
                          ns: 'cloudProcessing',
                        })}
                      </span>
                      <span>({t('optional', { ns: 'common' })})</span>
                    </Stack>
                  ),
                  info: (
                    <Trans
                      components={{ selectFilesButton: selectFilesButton }}
                      i18nKey="geodataSetup.dragAndDropInfo"
                      ns="cloudProcessing"
                    />
                  ),
                }}
              </IconPanel>
            </DragAndDropInfo>
          </Stack>
        )}

        {boundaryFiles.length > 0 && (
          <FilesContainer>
            <Stack alignItems="center" direction="row" justifyContent="space-between">
              <h4>{titleText}</h4>
              {selectFilesButton}
            </Stack>
            <DataTable
              columns={columns}
              fixedLayout={true}
              items={tableItems}
              pageSize={10}
              tableStyle={{
                rowCell: {
                  fontSize: '14px',
                },
                headerCell: {
                  fontSize: '14px',
                },
              }}
            />
          </FilesContainer>
        )}

        {error && (
          <ErrorInfoBox>
            <InfoBox color="red">{error}</InfoBox>
          </ErrorInfoBox>
        )}

        {isDropable && (
          <DropOverlay>
            <Icon color="green" icon={['fad', 'check']} size="5x" />
            {t('fileManager.uploadFilesDialog.dragAndDropDropToAdd', { ns: 'components' })}
          </DropOverlay>
        )}
      </Content>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const Content = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const DropOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  gap: 1em;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #fffe;
  z-index: 1;
`;

const DragAndDropInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px dashed #000;
  padding: 2em;
  height: 100%;
  width: 100%;
  flex: 1;

  button {
    margin: 0.75em auto 0;
  }
`;

const FilesContainer = styled(Stack)`
  flex: 1;
  overflow: hidden;
  min-height: 0;
  width: 100%;

  /* Prevent horizontal scrolling */
  > * {
    max-width: 100%;
    overflow-x: hidden;
  }
`;

const ErrorInfoBox = styled.div`
  margin-top: 1rem;
`;

export { UploadBoundary };
